import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Col, Collapse, Form, Row, Spin, Tooltip, Space, Input } from 'antd';
import moment from 'moment';
import { InfoCircleOutlined } from '@ant-design/icons';

import PoliciesOrderPanelComponent from './policies-order-panel';
import SignatureModal from './signature-modal';
import AppendFileModal from './appendFileModal';

import Filter from '../../../shared/components/filter';
import SelectWithFetchNext from '../../../shared/components/select-with-fetch-next';
import { filterTypes } from '../../../shared/constants/filterType';
import { isGuid } from '../../../shared/utils';

import * as sharedActions from '../../../shared/store/actions';
import { isArray, isEmpty } from 'lodash';

import '../styles.less';

const { Panel } = Collapse;

const fieldNames = Object.freeze({
  FormName: 'formName',
  ClaimIds: 'claimIds',
  InsuredContactIds: 'insuredContactIds',
  OwnerIds: 'ownerIds',
  DriverIds: 'driverIds',
  VehicleIds: 'vehicleIds',
  MotorTruckCargoIds: 'motorTruckCargoIds',
  PendingCancellationIds: 'pendingCancellationIds',
  LocationIds: 'locationIds',
  WorkerCompensationIds: 'workerCompensationIds',
  PropertyIds: 'propertyIds',
  AdditionalInterestIds: 'additionalInterestIds',
  GeneralLiabilityIds: 'generalLiabilityIds',
  ScheduleOfHazardIds: 'scheduleOfHazardIds',
  EquipmentIds: 'equipmentIds',
  PolicyIds: 'policyIds',
  AgentId: 'agentId',
  SignatureId: 'signatureId',
  AutomobileLiabilityCoverageSectionType: 'AutomobileLiabilityCoverageSectionType',
  SetAllQuestions: 'setAllQuestions',
  BoatIds: 'BoatIds',
  BusinessAutoAcord137Id: 'BusinessAutoAcord137Id',
  PersonalUmbrellaId: 'PersonalUmbrellaId',
  CommercialUmbrellaId: 'CommercialUmbrellaId',
});

let fetchDataDelay;

const PdfPrefillDataSourcesSelectorForm = ({
  rootContainer,
  triggerFormReload,
  contactId,
  clearFormFields,
  initialFormValues,
  policyId,
  permissions,
  registerGetFilterDataCallback,
  formParameters,
  formParametersLoading,
  setSignatureId,
  showSignatureOnPdf,
  onSaveSignatureClick,
  onCancelSignatureClick,
  onAppendFormSelected,
  isOriginalAcord,
  acordNameList,
  mainAcordName,
  isUserForm,
}) => {
  const [filterForm] = Form.useForm();

  const dispatch = useDispatch();

  const [policiesOrderer, setPoliciesOrderer] = useState();
  const [showPolicyOrders, setShowPolicyOrders] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showAppendFormModal, setShowAppendFormModal] = useState(false);
  const [formName, setFormName] = useState();

  const automobileLiabilityCoverageSectionTypes = useSelector((state) => state.shared.automobileLiabilityCoverageSectionTypes);
  const automobileLiabilityCoverageSectionTypesLoading = useSelector((state) => state.shared.automobileLiabilityCoverageSectionTypesLoading);

  const getFormData = () => {
    const formData = { ...filterForm.getFieldsValue() };

    if (Array.isArray(policiesOrderer) && policiesOrderer.length > 0) {
      formData.policiesOrderer = policiesOrderer.map((policyOrderer) => policyOrderer.key).filter((x) => isGuid(x));
    }

    return formData;
  };

  const fetchData = (formData) => {
    triggerFormReload({ ...formData });
  };

  useEffect(() => {
    if (initialFormValues.policyIds?.length > 1) {
      setShowPolicyOrders(true);
    } else {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (loaded) {
      if (typeof registerGetFilterDataCallback === 'function') {
        registerGetFilterDataCallback(getFormData);
      }

      fetchData(getFormData());
    }
  }, [loaded]);

  useEffect(() => {
    if (loaded) {
      handleFormSelectionChange();
    } else if (policiesOrderer) {
      setLoaded(true);
    }
  }, [policiesOrderer]);

  useEffect(() => {
    if (!showSignatureOnPdf) {
      filterForm.resetFields([fieldNames.SignatureId]);
    }
  }, [showSignatureOnPdf]);

  useEffect(() => {
    if (showPolicyOrders === false) {
      setPoliciesOrderer(undefined);
    }
  }, [showPolicyOrders]);

  useEffect(() => {
    if (isArray(acordNameList)) {
      let newName = generateName(acordNameList);

      if (!isUserForm) {
        const currentTime = ` (${moment(new Date()).format('MM/DD/YYYY')})`;

        if ((newName + currentTime).length > 199) {
          newName = newName.substring(0, 199 - currentTime.length);
        }

        newName = newName + currentTime;
      }

      filterForm.setFieldsValue({ formName: newName });

      setFormName(newName);
    }
  }, [acordNameList, mainAcordName]);

  const clearForm = () => {
    setPoliciesOrderer(undefined);
    filterForm.resetFields();

    filterForm.setFieldsValue({ policyIds: undefined });
  };

  const handleFormSelectionChange = (changedFields, allFields) => {
    if (
      changedFields &&
      changedFields.length === 1 &&
      (changedFields[0].name[0] === fieldNames.FormName || changedFields[0].name[0] === fieldNames.SignatureId)
    ) {
      return;
    }

    const formData = getFormData();

    if (fetchDataDelay) {
      clearTimeout(fetchDataDelay);
      fetchDataDelay = undefined;
    }

    fetchDataDelay = setTimeout(() => fetchData(formData), 1500);
  };

  const onClearFormClicked = () => {
    clearForm();
    clearFormFields();
  };

  const onResetFormClicked = () => {
    clearForm();

    const formData = getFormData();

    formData.isReset = true;

    clearFormFields(() => fetchData(formData));
  };

  const onSignatureChange = (value, options) => {
    setSignatureId(value.key);
  };

  const formData = loaded && filterForm.__INTERNAL__.name ? filterForm.getFieldsValue() : {};

  const onCreateSignatureClick = () => {
    setShowSignatureModal(true);
  };

  const signatureButtons =
    showSignatureOnPdf && formParameters && formParameters.showSignature ? (
      <>
        <Button type="primary" onClick={onSaveSignatureClick}>
          Save Signature
        </Button>
        <Button type="primary" danger onClick={onCancelSignatureClick}>
          Cancel Adding Signature
        </Button>
      </>
    ) : formParameters && formParameters.showSignature ? (
      <Button disabled={!permissions || !permissions.permissions_AcordForms_Signatures.canAdd} onClick={onCreateSignatureClick}>
        Create new Signature
      </Button>
    ) : null;

  const onCloseCreateSignatureModal = () => {
    setShowSignatureModal(false);
  };

  const onSignatureCreate = () => {
    dispatch(sharedActions.getSignaturesLookup({ pageNumber: 1, pageSize: 15 }));
  };

  const onAppendFormClicked = () => {
    setShowAppendFormModal(true);
  };

  const onCloseAppendFormModal = () => {
    setShowAppendFormModal(false);
  };

  const onPolicyChange = (value) => {
    setShowPolicyOrders(value.length > 1);
  };

  const onSelectDropdownItems = (value, formItemName) => {
    filterForm.setFieldValue(
      formItemName,
      value?.map((x) => x.key),
    );

    handleFormSelectionChange();
  };

  const generateName = (acordNameList) => {
    const extractedNames = acordNameList.map((acordName) => {
      const normalizedFile = acordName.replace(/\t/g, '').trim();

      if (!normalizedFile.startsWith('Acord') && !normalizedFile.startsWith('ACORD')) {
        return normalizedFile;
      }

      if (!/\s/.test(normalizedFile)) {
        return normalizedFile;
      }

      const match = normalizedFile.match(/^Acord\s*(\d+)/);
      if (match) {
        return match[1];
      }

      const match2 = normalizedFile.match(/^ACORD\s*(\d+)/);
      if (match2) {
        return match2[1];
      }

      return normalizedFile;
    });

    return [mainAcordName, ...extractedNames].join(' + ');
  };

  return (
    <Collapse defaultActiveKey="1">
      <Panel header="Data sources" key="1" className="pdf-prefill-filter-panel" forceRender>
        <Form
          name={'dataSource'}
          form={filterForm}
          size="small"
          className="pdf-prefill-filter-form"
          id="pdf-prefill-filter-form"
          layout="vertical"
          onFieldsChange={handleFormSelectionChange}
          initialValues={initialFormValues}
        >
          <Spin spinning={formParametersLoading || !formParameters}>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name={[fieldNames.FormName]}
                  label="Name"
                  className="filter-form-item"
                  rules={[
                    { required: true, message: 'Please input form name' },
                    { type: 'string', max: 199, message: 'The Form Name must be at most 199 characters long' },
                  ]}
                  tooltip={{
                    title: `Form name. This will be the name of the form when it is saved. Maximum 199 characters.`,
                    icon: <InfoCircleOutlined />,
                    getPopupContainer: rootContainer,
                  }}
                >
                  <Input type={'text'} placeholder={`Enter Name`} disabled={showSignatureOnPdf} value={formName} onChange={setFormName} />
                </Form.Item>
              </Col>

              {formParameters && formParameters.showPolicies ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getPoliciesLookup({
                        contactId,
                        includeInactivePolicies: true,
                        policyIds:
                          initialFormValues && isArray(initialFormValues.policyIds) && !isEmpty(initialFormValues.policyIds)
                            ? initialFormValues.policyIds
                            : isGuid(policyId)
                            ? [policyId]
                            : undefined,
                        ...data,
                      })
                    }
                    placeholder={'Policies'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.PolicyIds}
                    putInFormItem
                    fetchImmediately={isGuid(policyId) || isArray(initialFormValues?.policyIds)}
                    enableSelectAll
                    onSelect={onPolicyChange}
                    onSelectAll={onSelectDropdownItems}
                    initialData={(initialFormValues?.policyIds ?? [policyId]).map((x) => {
                      return { key: x, value: 'Loading...' };
                    })}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.showClaim ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getClaims({
                        contactId,
                        policiesIds: formData.policyIds,
                        claimType: formParameters.claimType,
                        ...data,
                      })
                    }
                    placeholder="Claims"
                    multipleMode
                    disabled={showSignatureOnPdf}
                    subscribeDataChangingAndFetchNewData={formData.policyIds}
                    formItemName={fieldNames.ClaimIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showContacts ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getInsuredContactsLookup({
                        insuredId: contactId,
                        ...data,
                      })
                    }
                    placeholder="Contacts"
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.InsuredContactIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showPrincipals ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getInsuredContactsOwnersLookup({
                        insuredId: contactId,
                        ...data,
                      })
                    }
                    placeholder="Principals or Owners"
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.OwnerIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showDrivers ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getDriversLookup({
                        insuredId: contactId,
                        policyId: policyId,
                        ...data,
                      })
                    }
                    placeholder={'Drivers'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.DriverIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showVehicles ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getVehiclesLookup({
                        insuredId: contactId,
                        policyId: policyId,
                        ...data,
                      })
                    }
                    placeholder={'Vehicles'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.VehicleIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showMotorTruckCargos ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getMotorTruckCargoLookup({
                        insuredId: contactId,
                        policyId: policyId,
                        ...data,
                      })
                    }
                    placeholder={'Motor Truck Cargos'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.MotorTruckCargoIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showPendingCancellations ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getPendingCancellationsLookup({
                        insuredId: contactId,
                        policyId: policyId,
                        ...data,
                      })
                    }
                    placeholder={'Pending Cancellations'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.PendingCancellationIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showLocations ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getTruckingCompanyContactsLookup({
                        insuredId: contactId,
                        policyId: policyId,
                        ...data,
                      })
                    }
                    placeholder={'Locations'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.LocationIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showWorkerCompensations ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getWorkersCompensationsLookup({
                        insuredId: contactId,
                        ...data,
                      })
                    }
                    placeholder={'Worker Compensations'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.WorkerCompensationIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showProperties ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getPropertiesLookup({
                        insuredId: contactId,
                        policyId: policyId,
                        ...data,
                      })
                    }
                    placeholder={'Properties'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.PropertyIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showAdditionalInterests ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getAdditionalInterestsLookup({
                        insuredId: contactId,
                        policyId: policyId,
                        ...data,
                      })
                    }
                    placeholder={'Additional Interests'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.AdditionalInterestIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showMultipleGeneralLiabilities ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getGeneralLiabilitiesLookup({
                        insuredId: contactId,
                        policyId,
                        ...data,
                      })
                    }
                    placeholder={'General Liability Rating'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.GeneralLiabilityIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showMultipleScheduleOfHazards ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getScheduleOfHazardsLookup({
                        insuredId: contactId,
                        policyId,
                        ...data,
                      })
                    }
                    placeholder={'Schedule of hazards'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.ScheduleOfHazardIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showEquipments ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getEquipmentsLookup({
                        insuredId: contactId,
                        policyId,
                        ...data,
                      })
                    }
                    placeholder={'Equipments'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.EquipmentIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showAgent ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={sharedActions.getAgentsLookup}
                    placeholder={'Agent'}
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.AgentId}
                    putInFormItem
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showAutomobileLiabilityCoverageSectionType ? (
                <Filter
                  rootContainer={rootContainer}
                  filterType={filterTypes.dropdownWithServerData}
                  name={fieldNames.AutomobileLiabilityCoverageSectionType}
                  label="Automobile Liability coverage section"
                  span={12}
                  data={automobileLiabilityCoverageSectionTypes}
                  dataLoading={automobileLiabilityCoverageSectionTypesLoading}
                  dataSearchAction={sharedActions.getAutomobileLiabilityCoverageSectionTypes}
                  maxTagCount={1}
                  mode="single"
                  filterByChildData
                  disabled={showSignatureOnPdf}
                />
              ) : null}

              {formParameters && formParameters.formLookupData.showBoats ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) =>
                      sharedActions.getBoatsLookup({
                        insuredId: contactId,
                        policyId,
                        ...data,
                      })
                    }
                    placeholder={'Boats'}
                    multipleMode
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.BoatIds}
                    putInFormItem
                    enableSelectAll
                    onSelectAll={onSelectDropdownItems}
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showBusinessAutoAcord137 ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) => sharedActions.getBusinessAutoAcord137sLookup({ insuredId: contactId, ...data })}
                    placeholder={'Business Auto - Acord 137'}
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.BusinessAutoAcord137Id}
                    putInFormItem
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showPersonalUmbrella ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) => sharedActions.getPersonalUmbrellasLookup({ insuredId: contactId, ...data })}
                    placeholder={'Personal Umbrellas'}
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.PersonalUmbrellaId}
                    putInFormItem
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showCommercialUmbrella ? (
                <Col span={12}>
                  <SelectWithFetchNext
                    rootContainer={rootContainer}
                    fetchNext={(data) => sharedActions.getCommercialUmbrellasLookup({ insuredId: contactId, ...data })}
                    placeholder={'Commercial Umbrellas'}
                    disabled={showSignatureOnPdf}
                    formItemName={fieldNames.CommercialUmbrellaId}
                    putInFormItem
                  />
                </Col>
              ) : null}

              {formParameters && formParameters.formLookupData.showSetAllQuestions ? (
                <Filter
                  rootContainer={rootContainer}
                  filterType={filterTypes.dropdownYesNo}
                  name={fieldNames.SetAllQuestions}
                  label="Set all questions to"
                  span={12}
                  disabled={showSignatureOnPdf}
                />
              ) : null}

              {formParameters && formParameters.showSignature ? (
                <Col span={12}>
                  <Form.Item>
                    <Col span={24}>
                      <SelectWithFetchNext
                        rootContainer={rootContainer}
                        fetchNext={sharedActions.getSignaturesLookup}
                        placeholder="Signature"
                        formItemName={fieldNames.SignatureId}
                        putInFormItem
                        onSelect={onSignatureChange}
                      />
                    </Col>
                    <Form.Item style={{ marginTop: '7px' }}>
                      <Space>{signatureButtons}</Space>
                    </Form.Item>
                  </Form.Item>
                </Col>
              ) : null}
            </Row>

            {showPolicyOrders ? (
              <Row style={{ marginTop: '8px' }}>
                <Col span={24}>
                  <PoliciesOrderPanelComponent
                    policiesIds={filterForm.getFieldValue(fieldNames.PolicyIds)}
                    policiesOrderer={policiesOrderer}
                    setPoliciesOrderer={setPoliciesOrderer}
                    disabled={showSignatureOnPdf}
                  />
                </Col>
              </Row>
            ) : null}

            <Row style={{ marginTop: '8px' }}>
              <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                  {isOriginalAcord ? (
                    <Tooltip placement="bottomRight" title="Append new form to this form" getPopupContainer={rootContainer}>
                      <Button type="primary" style={{ float: 'right' }} onClick={onAppendFormClicked}>
                        Append
                      </Button>
                    </Tooltip>
                  ) : undefined}

                  <Tooltip placement="bottomRight" title="This will clear the form of all pre-filled fields" getPopupContainer={rootContainer}>
                    <Button
                      type="primary"
                      danger
                      style={{ float: 'right' }}
                      onClick={onClearFormClicked}
                      loading={formParametersLoading}
                      disabled={showSignatureOnPdf}
                    >
                      Clear
                    </Button>
                  </Tooltip>

                  {isUserForm ? undefined : (
                    <Tooltip
                      placement="bottomRight"
                      title="This will clear the form of all pre-filled fields and prefill form by default values"
                      getPopupContainer={rootContainer}
                    >
                      <Button
                        type="primary"
                        danger
                        style={{ float: 'right' }}
                        onClick={onResetFormClicked}
                        loading={formParametersLoading}
                        disabled={showSignatureOnPdf}
                      >
                        Reset
                      </Button>
                    </Tooltip>
                  )}
                </Space>
              </Col>
            </Row>
          </Spin>
        </Form>

        {showSignatureModal ? (
          <SignatureModal
            closeModal={onCloseCreateSignatureModal}
            rootContainer={rootContainer}
            onSignatureCreate={onSignatureCreate}
            truckingCompanyId={contactId}
          />
        ) : null}

        {showAppendFormModal ? (
          <AppendFileModal
            rootContainer={rootContainer}
            open={showAppendFormModal}
            onClose={onCloseAppendFormModal}
            onAppendFormSelected={onAppendFormSelected}
          />
        ) : undefined}
      </Panel>
    </Collapse>
  );
};

export default PdfPrefillDataSourcesSelectorForm;
